import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import NavHeader from "@/components/NavHeader";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import CombatArena from "@/components/CombatArena";
import {
  Users, Crown, Skull, Edit3, LogOut, Trash2, RefreshCw, Copy,
  Shield, ShieldOff, UserMinus, Swords, Save, ScrollText, Link as LinkIcon, ChevronLeft, Plus, Minus,
} from "lucide-react";

export default function Chronicle() {
  const { id } = useParams();
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  const [data, setData] = useState(null);   // {chronicle, characters, encounters, is_gm, is_owner}
  const [busy, setBusy] = useState(true);
  const [gmMode, setGmMode] = useState(false);
  const [myChars, setMyChars] = useState([]);
  const [notes, setNotes] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);
  const [encounters, setEncounters] = useState([]);   // user's own encounters (for attaching)
  const [arenaOpen, setArenaOpen] = useState(false);
  const [arenaRoster, setArenaRoster] = useState([]);

  const load = useCallback(async () => {
    if (!user) return;
    setBusy(true);
    try {
      const [r, rc, re] = await Promise.all([
        api.get(`/chronicles/${id}`),
        api.get(`/characters`),
        api.get(`/encounters`).catch(() => ({ data: [] })),
      ]);
      setData(r.data);
      setNotes(r.data.chronicle.gm_notes || "");
      setMyChars(rc.data || []);
      setEncounters(re.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load chronicle");
      navigate("/chronicles");
    } finally {
      setBusy(false);
    }
  }, [id, user, navigate]);

  useEffect(() => { if (!loading) load(); }, [loading, load]);

  useEffect(() => {
    // Default to GM mode if you're a GM
    if (data?.is_gm) setGmMode(true);
  }, [data?.is_gm]);

  if (loading || busy) {
    return (
      <div className="min-h-screen scanlines">
        <NavHeader />
        <div className="max-w-[1400px] mx-auto px-4 py-8 font-mono text-sm text-[color:var(--text-muted)]">LOADING…</div>
      </div>
    );
  }
  if (!data) return null;

  const chron = data.chronicle;
  const isGm = data.is_gm;
  const isOwner = data.is_owner;
  const gmModeActive = isGm && gmMode;

  const myMember = chron.members.find((m) => m.user_id === user.user_id);
  const myCharacterId = myMember?.character_id || "";

  const shareLink = `${window.location.origin}/chronicle/join/${chron.code}`;

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(chron.code);
      toast.success("CODE COPIED");
    } catch { toast.error("Copy failed"); }
  };
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareLink);
      toast.success("INVITE LINK COPIED");
    } catch { toast.error("Copy failed"); }
  };

  const changeMyChar = async (charId) => {
    try {
      await api.post(`/chronicles/${chron.id}/select-character`, { character_id: charId || null });
      toast.success("CHARACTER LOCKED IN");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const saveNotes = async () => {
    setSavingNotes(true);
    try {
      await api.put(`/chronicles/${chron.id}`, { gm_notes: notes });
      toast.success("NOTES SAVED");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    setSavingNotes(false);
  };

  const leave = async () => {
    if (!window.confirm("LEAVE THIS CHRONICLE?")) return;
    try {
      await api.post(`/chronicles/${chron.id}/leave`);
      toast("YOU LEFT THE CHRONICLE");
      navigate("/chronicles");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const del = async () => {
    if (!window.confirm("DELETE CHRONICLE FOR EVERYONE? PERMANENT.")) return;
    try {
      await api.delete(`/chronicles/${chron.id}`);
      toast("CHRONICLE ERASED");
      navigate("/chronicles");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const kick = async (uid) => {
    if (!window.confirm("KICK THIS MEMBER?")) return;
    try {
      await api.post(`/chronicles/${chron.id}/kick`, { user_id: uid });
      toast("MEMBER REMOVED");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const togglePromote = async (uid) => {
    try {
      await api.post(`/chronicles/${chron.id}/promote`, { user_id: uid });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const attachEnc = async (encId) => {
    try {
      await api.post(`/chronicles/${chron.id}/attach-encounter`, { encounter_id: encId });
      toast.success("ENCOUNTER LINKED");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const detachEnc = async (encId) => {
    try {
      await api.post(`/chronicles/${chron.id}/detach-encounter`, { encounter_id: encId });
      toast("ENCOUNTER UNLINKED");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const editPlayerHP = async (charId, delta) => {
    const ch = data.characters[charId];
    if (!ch) return;
    const next = Math.max(-99, Math.min(ch.hp_max ?? 99, (ch.hp_current || 0) + delta));
    try {
      await api.put(`/chronicles/${chron.id}/characters/${charId}`, { hp_current: next });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "HP edit failed"); }
  };

  const editPlayerGlitches = async (charId, delta) => {
    const ch = data.characters[charId];
    if (!ch) return;
    const next = Math.max(0, (ch.glitches_count || 0) + delta);
    try {
      await api.put(`/chronicles/${chron.id}/characters/${charId}`, { glitches_count: next });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Glitch edit failed"); }
  };

  const openPunkSheet = (charId) => {
    // GM opens any punk; owner can open their own via same route.
    // If gm and not owner, open dedicated GM viewer (share as a preview)
    const ch = data.characters[charId];
    if (!ch) return;
    if (ch.user_id === user.user_id) {
      navigate(`/character/${charId}`);
    } else if (isGm) {
      navigate(`/chronicle/${chron.id}/character/${charId}`);
    } else {
      toast("Not your character");
    }
  };

  const runArena = () => {
    // Roster = every punk in the chronicle that we can load
    const roster = Object.values(data.characters);
    if (roster.length === 0) { toast.error("No punks in this chronicle yet"); return; }
    setArenaRoster(roster);
    setArenaOpen(true);
  };

  return (
    <div className="min-h-screen scanlines">
      <NavHeader />
      <main className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6">
        <button
          data-testid="back-chronicles-btn"
          onClick={() => navigate("/chronicles")}
          className="cy-btn text-xs mb-4"
        >
          <ChevronLeft size={14} className="mr-1" /> CHRONICLES
        </button>

        {/* HEADER */}
        <div className="panel panel-cyan p-5 sm:p-6 mb-6" data-testid="chronicle-header">
          <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <span className="tape tape-magenta">// CHRONICLE</span>
                {isOwner && <span className="tape"><Crown size={10} className="inline mr-1" /> OWNER</span>}
                {isGm && !isOwner && <span className="tape tape-cyan">GM</span>}
                {!isGm && <span className="tape">PLAYER</span>}
              </div>
              <h1 className="font-display text-3xl sm:text-5xl font-black uppercase tracking-tighter leading-none">
                {chron.name}
              </h1>
              {chron.description && (
                <p className="font-mono text-sm text-[color:var(--text-secondary)] mt-2 max-w-2xl">
                  {chron.description}
                </p>
              )}
            </div>
            <div className="flex flex-col items-stretch sm:items-end gap-2 sm:min-w-[240px]">
              <div className="flex items-center gap-2">
                <div
                  data-testid="chronicle-code"
                  className="font-display text-3xl font-black tracking-widest bg-[color:var(--bg-void)] border-2 border-[color:var(--border-hard)] px-3 py-1 text-[color:var(--accent-yellow)]"
                >
                  {chron.code}
                </div>
                <button data-testid="copy-code-btn" onClick={copyCode} className="cy-btn cy-btn-cyan text-xs" aria-label="Copy code">
                  <Copy size={14} />
                </button>
              </div>
              <button data-testid="copy-link-btn" onClick={copyLink} className="cy-btn text-xs">
                <LinkIcon size={12} className="mr-1" /> COPY INVITE LINK
              </button>
              <div className="flex items-center gap-2 flex-wrap">
                <button data-testid="refresh-chronicle-btn" onClick={load} className="cy-btn text-xs">
                  <RefreshCw size={12} className="mr-1" /> REFRESH
                </button>
                {isGm && (
                  <label className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest cursor-pointer select-none" data-testid="gm-mode-toggle">
                    <input
                      type="checkbox"
                      checked={gmMode}
                      onChange={(e) => setGmMode(e.target.checked)}
                      className="accent-[color:var(--accent-magenta)]"
                    />
                    GM MODE
                  </label>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* MY SEAT */}
        <div className="panel p-4 sm:p-5 mb-6" data-testid="my-seat-panel">
          <div className="flex flex-wrap items-center gap-2 mb-3">
              <span className="tape">// MY SEAT</span>
            <span className="font-mono text-xs text-[color:var(--text-muted)] uppercase tracking-widest">
              Pick which punk you&apos;re playing in this chronicle
            </span>
          </div>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
            <select
              data-testid="my-character-select"
              value={myCharacterId}
              onChange={(e) => changeMyChar(e.target.value)}
              className="cy-input flex-1 max-w-md"
            >
              <option value="">— NO CHARACTER (GM / SPECTATOR) —</option>
              {myChars.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name || "UNNAMED"} · {c.char_class || "UNCLASS"} · HP {c.hp_current}/{c.hp_max}
                </option>
              ))}
            </select>
            {myCharacterId && (
              <button
                data-testid="open-my-sheet-btn"
                onClick={() => navigate(`/character/${myCharacterId}`)}
                className="cy-btn cy-btn-cyan text-xs"
              >
                <Edit3 size={12} className="mr-1" /> OPEN SHEET
              </button>
            )}
          </div>
        </div>

        {/* CREW GRID */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display text-2xl sm:text-3xl font-black uppercase tracking-tighter flex items-center gap-2">
              <Users className="text-[color:var(--accent-cyan)]" /> CREW
            </h2>
            <span className="tape">{chron.members.length}</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="crew-grid">
            {chron.members.map((m) => {
              const memberIsOwner = m.user_id === chron.owner_user_id;
              const memberIsGm = (chron.gms || []).includes(m.user_id);
              const ch = m.character_id ? data.characters[m.character_id] : null;
              return (
                <div
                  key={m.user_id}
                  className={`panel p-4 ${memberIsGm ? "panel-magenta" : ""}`}
                  data-testid={`crew-card-${m.user_id}`}
                >
                  <div className="flex items-start justify-between mb-2 gap-2">
                    <div className="min-w-0">
                      <div className="text-[10px] font-mono uppercase tracking-widest text-[color:var(--accent-cyan)] flex items-center gap-1">
                        {memberIsOwner && <><Crown size={10} /> OWNER</>}
                        {!memberIsOwner && memberIsGm && <>GM</>}
                        {!memberIsGm && <>PLAYER</>}
                      </div>
                      <div className="font-display text-lg font-black uppercase tracking-tight truncate">
                        {m.name || m.email || "ANON"}
                      </div>
                    </div>
                  </div>
                  {ch ? (
                    <div className="border-t-2 border-dashed border-[color:var(--border-hard)] pt-2 mt-2">
                      <div className="flex items-center justify-between mb-2">
                        <div className="min-w-0">
                          <div className="text-[9px] font-mono uppercase tracking-widest text-[color:var(--text-muted)] truncate">
                            {ch.char_class || "UNCLASS"}
                          </div>
                          <div className="font-display text-base font-black uppercase tracking-tight truncate">
                            {ch.name || "UNNAMED"}
                          </div>
                        </div>
                        <div className="tape tape-magenta text-[10px]">HP {ch.hp_current}/{ch.hp_max}</div>
                      </div>
                      <div className="grid grid-cols-5 gap-1 mb-2 text-center">
                        {["presence","strength","agility","toughness","knowledge"].map((k) => (
                          <div key={k} className="border-2 border-[color:var(--border-hard)] p-1">
                            <div className="text-[7px] font-mono uppercase text-[color:var(--text-muted)]">{k.slice(0,3)}</div>
                            <div className="font-display text-sm text-[color:var(--accent-yellow)]">{(ch[k]||0)>=0?"+":""}{ch[k]||0}</div>
                          </div>
                        ))}
                      </div>
                      <div className="flex items-center justify-between text-[10px] font-mono uppercase text-[color:var(--text-secondary)]">
                        <span>GLITCHES {ch.glitches_count || 0}</span>
                        <span>SATS {ch.sats || 0}</span>
                      </div>

                      {gmModeActive && (
                        <div className="mt-3 pt-2 border-t-2 border-dashed border-[color:var(--border-hard)]">
                          <div className="text-[9px] font-mono uppercase tracking-widest text-[color:var(--accent-magenta)] mb-1">GM CONTROLS</div>
                          <div className="flex flex-wrap items-center gap-1">
                            <button data-testid={`gm-hp-minus-${m.user_id}`} onClick={() => editPlayerHP(ch.id, -1)} className="cy-btn text-[10px] px-2 py-1"><Minus size={10} /> HP</button>
                            <button data-testid={`gm-hp-plus-${m.user_id}`} onClick={() => editPlayerHP(ch.id, +1)} className="cy-btn text-[10px] px-2 py-1"><Plus size={10} /> HP</button>
                            <button data-testid={`gm-glitch-minus-${m.user_id}`} onClick={() => editPlayerGlitches(ch.id, -1)} className="cy-btn cy-btn-magenta text-[10px] px-2 py-1"><Minus size={10} /> G</button>
                            <button data-testid={`gm-glitch-plus-${m.user_id}`} onClick={() => editPlayerGlitches(ch.id, +1)} className="cy-btn cy-btn-magenta text-[10px] px-2 py-1"><Plus size={10} /> G</button>
                            <button data-testid={`gm-open-sheet-${m.user_id}`} onClick={() => openPunkSheet(ch.id)} className="cy-btn cy-btn-cyan text-[10px] px-2 py-1"><Edit3 size={10} className="mr-1" /> EDIT SHEET</button>
                          </div>
                        </div>
                      )}
                      {!gmModeActive && ch.user_id === user.user_id && (
                        <button onClick={() => openPunkSheet(ch.id)} className="cy-btn text-xs w-full mt-2">
                          <Edit3 size={12} className="mr-1" /> OPEN
                        </button>
                      )}
                    </div>
                  ) : (
                    <div className="text-[10px] font-mono uppercase tracking-widest text-[color:var(--text-muted)] italic mt-2">
                      NO CHARACTER SELECTED
                    </div>
                  )}

                  {gmModeActive && isOwner && !memberIsOwner && (
                    <div className="mt-3 flex items-center gap-1">
                      <button
                        data-testid={`gm-promote-${m.user_id}`}
                        onClick={() => togglePromote(m.user_id)}
                        className="cy-btn text-[10px] px-2 py-1 flex-1"
                        aria-label="Toggle GM"
                      >
                        {memberIsGm ? <><ShieldOff size={10} className="mr-1" /> DEMOTE</> : <><Shield size={10} className="mr-1" /> PROMOTE</>}
                      </button>
                      <button
                        data-testid={`gm-kick-${m.user_id}`}
                        onClick={() => kick(m.user_id)}
                        className="cy-btn cy-btn-magenta text-[10px] px-2 py-1"
                        aria-label="Kick"
                      >
                        <UserMinus size={10} />
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* GM SECTION */}
        {gmModeActive && (
          <>
            <div className="panel panel-magenta p-4 sm:p-5 mb-6" data-testid="gm-notes-panel">
              <div className="flex items-center justify-between mb-2">
                <h2 className="font-display text-xl sm:text-2xl font-black uppercase tracking-tight flex items-center gap-2">
                  <ScrollText className="text-[color:var(--accent-magenta)]" /> GM NOTES <span className="text-[10px] font-mono text-[color:var(--text-muted)]">// PRIVATE TO GMs</span>
                </h2>
                <button data-testid="save-notes-btn" onClick={saveNotes} disabled={savingNotes} className="cy-btn cy-btn-solid-yellow text-xs">
                  <Save size={12} className="mr-1" /> {savingNotes ? "..." : "SAVE"}
                </button>
              </div>
              <textarea
                data-testid="gm-notes-textarea"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={6}
                placeholder="Session hooks, secret DCs, boss weaknesses..."
                className="cy-input w-full"
              />
            </div>

            <div className="panel p-4 sm:p-5 mb-6" data-testid="encounters-panel">
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-display text-xl sm:text-2xl font-black uppercase tracking-tight flex items-center gap-2">
                  <Swords className="text-[color:var(--accent-yellow)]" /> ENCOUNTERS
                </h2>
                <button data-testid="run-arena-btn" onClick={runArena} className="cy-btn cy-btn-magenta text-xs">
                  <Swords size={12} className="mr-1" /> RUN ARENA WITH CREW
                </button>
              </div>

              {data.encounters.length === 0 && (
                <div className="font-mono text-xs text-[color:var(--text-muted)] mb-3">No encounters linked yet.</div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 mb-4">
                {data.encounters.map((e) => (
                  <div key={e.id} className="border-2 border-[color:var(--border-hard)] p-3 flex items-center justify-between gap-2" data-testid={`linked-encounter-${e.id}`}>
                    <div className="min-w-0">
                      <div className="font-display text-sm font-black uppercase tracking-tight truncate">{e.name}</div>
                      <div className="text-[10px] font-mono text-[color:var(--text-muted)]">R{e.round} · {e.combatant_count} combatants</div>
                    </div>
                    <button
                      onClick={() => detachEnc(e.id)}
                      className="cy-btn cy-btn-magenta text-[10px] px-2 py-1"
                      aria-label="Unlink"
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                ))}
              </div>

              {encounters.length > 0 && (
                <>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-[color:var(--text-muted)] mb-1">ATTACH FROM YOUR SAVED ENCOUNTERS:</div>
                  <div className="flex flex-wrap gap-1">
                    {encounters
                      .filter((e) => !(chron.encounter_ids || []).includes(e.id))
                      .map((e) => (
                        <button
                          key={e.id}
                          data-testid={`attach-encounter-${e.id}`}
                          onClick={() => attachEnc(e.id)}
                          className="cy-btn text-[10px] px-2 py-1"
                        >
                          <Plus size={10} className="mr-1" /> {e.name}
                        </button>
                      ))}
                  </div>
                </>
              )}
            </div>
          </>
        )}

        {/* DANGER ZONE */}
        <div className="panel p-4 sm:p-5">
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
            <div className="font-mono text-[11px] uppercase tracking-widest text-[color:var(--text-muted)]">
              INVITE CODE: <span className="text-[color:var(--accent-yellow)] font-display text-base ml-2">{chron.code}</span>
            </div>
            <div className="flex items-center gap-2">
              {!isOwner && (
                <button data-testid="leave-chronicle-btn" onClick={leave} className="cy-btn cy-btn-magenta text-xs">
                  <LogOut size={12} className="mr-1" /> LEAVE
                </button>
              )}
              {isOwner && (
                <button data-testid="delete-chronicle-btn" onClick={del} className="cy-btn cy-btn-magenta text-xs">
                  <Trash2 size={12} className="mr-1" /> DELETE CHRONICLE
                </button>
              )}
            </div>
          </div>
        </div>
      </main>

      <CombatArena
        open={arenaOpen}
        onClose={() => { setArenaOpen(false); load(); }}
        roster={arenaRoster}
      />
    </div>
  );
}
